import React, { useEffect, useState } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import toastr from "toastr";

const GenerateDossierModal = ({
  show,
  onClose,
  candidateId,
  onGenerated,
  roles,
  role_id,
  role_name,
}) => {
  const { token, setGlobalLoading } = useOutletContext();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [selectedRole, setSelectedRole] = useState(role_id || "");
  const [criteriaText, setCriteriaText] = useState("");
  const [gender, setGender] = useState("");
  const [recordingFile, setRecordingFile] = useState(null);
  const [fileToSubmit, setFileToSubmit] = useState(null);
  const [recordingExists, setRecordingExists] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const [transcribing, setTranscribing] = useState(false); // NEW

  // Set initial role if provided
  useEffect(() => {
    if (role_id) setSelectedRole(role_id);
  }, [role_id]);

  // Fetch role criteria and recording existence
  useEffect(() => {
    if (!selectedRole) {
      setCriteriaText("");
      setRecordingExists(false);
      return;
    }

    const fetchRoleData = async () => {
      try {
        setGlobalLoading(true);
        const res = await axios.get(
          `${API_BASE_URL}/candidates/get_recording_role_criteria/${candidateId}/${selectedRole}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setRecordingExists(res.data.recordingExists);
        const criteriaArray = res.data.keywords || [];
        setCriteriaText(criteriaArray.map((c) => c.name).join("\n"));
      } catch (err) {
        console.error("Error fetching role data:", err);
        toastr.error("Failed to fetch role data");
      } finally {
        setGlobalLoading(false);
      }
    };

    fetchRoleData();
  }, [selectedRole, candidateId]);

  // Upload recording and trigger transcription
  useEffect(() => {
    if (!fileToSubmit) return;

    const uploadRecording = async () => {
      setGlobalLoading(true);
      try {
        const formData = new FormData();
        formData.append("candidateId", candidateId);
        formData.append("roleId", selectedRole);
        formData.append("user", token);
        formData.append("recording", fileToSubmit);

        const response = await axios.post(
          `${API_BASE_URL}/candidates/upload_candidate_recording`,
          formData,
          
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        console.log("Upload API Response:", response.data);

       if (response.status === 200) {
  toastr.success("Recording uploaded successfully.");

  // 👇 GET FILENAME RETURNED FROM UPLOAD API
  const uploadedFilename = response.data.file;

  setRecordingFile(null);
  setFileToSubmit(null);
  setRecordingExists(true);

  try {
    setTranscribing(true);

    // 🔹 Start transcription (same flow as old working code)
    await axios.post(
      `${API_BASE_URL}/chatgpt/whisper_transcribe`,
      {
        filename: uploadedFilename,
        candidate_id: candidateId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // 🔹 Poll until transcription exists
    let attempts = 0;
    const maxAttempts = 20;

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/candidates/get_transcription/${candidateId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data.transcription) {
          setTranscription(res.data.transcription);
          setTranscribing(false);
          clearInterval(interval);
        }
      } catch (err) {
        attempts++;

        if (attempts >= maxAttempts) {
          setTranscribing(false);
          clearInterval(interval);
          toastr.error("Transcription took too long.");
        }
      }
    }, 3000);

  } catch (transErr) {
    console.error("Error generating transcription:", transErr);
    setTranscribing(false);
  }
}
      } catch (error) {
        console.error("Error uploading recording:", error);
        toastr.error("Failed to upload recording.");
      } finally {
        setGlobalLoading(false);
      }
    };

    uploadRecording();
  }, [fileToSubmit, candidateId, selectedRole]);

  // Handle dossier generation
  const handleGenerate = async () => {
    if (!selectedRole) return toastr.error("Please select a role");
    if (!gender) return toastr.error("Please select candidate gender");

    try {
      setGlobalLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/candidates/generate_dossier`,
        {
          candidate_id: candidateId,
          role_id: selectedRole,
          gender,
          role_criteria: criteriaText,
        },
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `dossier_${candidateId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toastr.success("Dossier generated successfully");
      onGenerated?.();
      onClose();
    } catch (error) {
      console.error("Error generating dossier:", error);
      toastr.error("Failed to generate dossier");
    } finally {
      setGlobalLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5>Select Role for Dossier</h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            {/* Role Dropdown */}
            {!role_id && (
              <select
                className="form-select mb-3"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="">Select Role</option>
                {roles?.map((role) => (
                  <option key={role?.role_id} value={role?.role_id}>
                    {role?.role}
                  </option>
                ))}
              </select>
            )}

            {role_id && (
              <div className="mb-3">
                <label className="form-label fw-bold">Role</label>
                <div className="form-control bg-light">{role_name}</div>
              </div>
            )}

            {/* Editable Criteria */}
            {selectedRole && (
              <div className="mb-3">
                <label className="form-label fw-bold">Edit Role Criteria</label>
                <textarea
                  className="form-control"
                  rows="6"
                  value={criteriaText}
                  onChange={(e) => setCriteriaText(e.target.value)}
                />
              </div>
            )}

            {/* Gender Selection */}
            {selectedRole && (
              <div className="mb-3">
                <label className="form-label fw-bold">Candidate Gender</label>
                <select
                  className="form-select"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            )}

            {/* Upload Recording if it does not exist */}
            {selectedRole && !recordingExists && (
              <div className="mb-3">
                <label className="form-label fw-bold">
                  Upload Recording (Optional)
                </label>
                <input
  type="file"
  className="form-control"
  accept="audio/*,video/*"
  onChange={(e) => {
    const file = e.target.files[0];
    setRecordingFile(file);
    setFileToSubmit(file); // 🔥 automatically trigger upload
  }}
/>
                {recordingFile && (
                  <div className="mt-2 small text-muted">
                    Selected: <strong>{recordingFile.name}</strong>
                  </div>
                )}
              </div>
            )}
            {transcribing && (
              <div className="text-info mt-2">
                ⏳ Generating transcription... Please wait
              </div>
            )}

            {/* Show transcription button if transcription exists */}
            {transcription && (
              <div className="mt-2">
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => setShowTranscriptionModal(true)}
                >
                  📄 Show Transcription
                </button>
              </div>
            )}

            {/* Transcription modal */}
            {showTranscriptionModal && (
              <div className="modal show d-block">
                <div className="modal-dialog">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5>Transcription</h5>
                      <button
                        className="btn-close"
                        onClick={() => setShowTranscriptionModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <pre>{transcription}</pre>
                    </div>
                    <div className="modal-footer">
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowTranscriptionModal(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary me-2" onClick={onClose}>
              Cancel
            </button>

           

            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={!selectedRole || !gender}
            >
              Generate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateDossierModal;
